/**
 * VideoCallPage — the 1:1 chat video-call screen (web port of Flutter
 * `video_call_screen.dart`). One page serves BOTH roles off the hook's
 * `isCaller` branch. Page owns the route-state snapshot, the `screenKey` mint,
 * the Agora client/provider, and the `useBasePage` lifecycle (forwarding the
 * hook's `onPageDispose`); `useVideoCall` owns the Agora orchestration, Firestore
 * side-effects, token lifecycle, and live subscription. Route args
 * (`location.state`) carry the full call context — NO by-id fetch (like
 * `MediaDetailsPage`). The `_lastGift` overlay (Flutter `:551-691`) is
 * implemented as `<VideoCallLastGift />`, rendered on the live surface and
 * driven by the hook's `latestLiveGift` / `latestLiveGiftOpacity` view-model.
 */

import React, { useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AgoraRTC, { AgoraRTCProvider } from 'agora-rtc-react';
import type { IAgoraRTCClient } from 'agora-rtc-react';

import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { Navigator } from '@/presentation/routing/Navigator';
import { BackIconButton } from '@/presentation/components/common/BackIconButton';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { ConfirmSheet } from '@/presentation/components/common/sheet/confirm/ConfirmSheet';
import { SendGiftSheet } from '@/presentation/feature/gift/components/SendGiftSheet';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { SendGiftTargetType } from '@/domain/enums/gift/sendGiftTargetType';
import { VideoCallStateVariant } from '@/domain/enums/videoCall/videoCallStateVariant';

import type { RootState } from '@/presentation/store';

import { useVideoCall } from './hooks/useVideoCall';
import { VideoCallStateView } from './components/VideoCallStateView';
import { VideoCallVideoViews } from './components/VideoCallVideoViews';
import { VideoCallControls } from './components/VideoCallControls';
import { VideoCallLastGift } from './components/VideoCallLastGift';
import type { VideoCallRouteArgs } from './args/videoCallRouteArgs';

/**
 * Inner body — runs INSIDE `<AgoraRTCProvider>` so `useVideoCall` (`useRTCClient()`)
 * and the video views can consume the Agora hooks. Owns the hook, the
 * `useBasePage` lifecycle bridge, and the rendered surface.
 */
const VideoCallPageBody: React.FC<{
  args: VideoCallRouteArgs;
  screenKey: string;
  currentUserFirebaseId: string;
}> = ({ args, screenKey, currentUserFirebaseId }) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  // The single place the videoCall feature touches routing. Passed into the hook
  // so its terminal paths (token-regen failure, initial-token failure, endCall)
  // pop without the hook reaching into the Navigator (Finding 12). Stable via
  // `useCallback` so the hook's dep arrays do not churn.
  const goBack = useCallback((): void => Navigator.goBack(), []);

  const videoCall = useVideoCall({
    callerUser: args.callerUser,
    audienceUser: args.audienceUser,
    groupChatId: args.groupChatId,
    callMessageId: args.callMessageId,
    callerUserStreamingId: args.callerUserStreamingId,
    chatUsersInfo: args.chatUsersInfo,
    goBack,
  });

  // Page-only `useBasePage` forwarding the hook's run-once `onPageInit` (Flutter
  // `_init` :124-141) and `onPageDispose` (Flutter `dispose` :143-168), symmetric.
  // `onPageInit` returns the init-side teardown `useBasePage` runs on unmount.
  useBasePage({
    screenKey,
    onInit: videoCall.onPageInit,
    onDispose: videoCall.onPageDispose,
  });

  // Coarse page-root status token for the qa-tester (precedence mirrors Flutter `:863-915`).
  const pageStatus = videoCall.audienceDidNotJoin
    ? 'audienceNoJoin'
    : videoCall.callEnded
      ? 'ended'
      : videoCall.isRenewingToken
        ? 'renewingToken'
        : videoCall.isOpeningChannel
          ? 'loading'
          : 'connected';

  const copy = l10n.chat.videoCallPage;

  // Shared end-call confirm gate (Flutter `:1219-1235`): show the confirm sheet,
  // and only on a confirmed (truthy) response leave + pop via `endCall` (which
  // itself pops, deferring teardown to dispose). Both the bottom-bar end-call
  // button and the top-bar back arrow route through this — the sheet-opening now
  // lives on the page (Finding 1) so the control bar stays presentational.
  const confirmThenEndCall = async (): Promise<void> => {
    const response = await ConfirmSheet.show({
      title: copy.confirmEndTitle,
      confirmText: copy.confirmEndDescription,
      okText: l10n.common.yes,
      cancelText: l10n.common.cancel,
      screenKey,
    });
    if (response) {
      videoCall.endCall();
    }
  };

  // Bottom-bar end-call button (Flutter `_streamActions` end-call, `:1232-1259`) —
  // confirm, then end. Passed down to `VideoCallControls` as `onEndCall`.
  const handleEndCall = (): Promise<void> => confirmThenEndCall();

  // Top-bar back arrow → confirm sheet (parity with the end-call button and the
  // corrected Flutter `_appBarLeading`). The live-call back arrow must NOT pop
  // directly — it ends an active call, so it gates on the same confirm the
  // bottom-bar end-call button uses. On confirm, end the call (which itself pops).
  const handleBack = async (): Promise<void> => {
    // If the call is already over (peer ended it, audience never joined, etc.),
    // there is nothing to confirm — just pop directly. The terminal state views
    // are showing, so "End video call?" would make no sense.
    if (videoCall.callEnded || videoCall.audienceDidNotJoin) {
      goBack();
      return;
    }
    await confirmThenEndCall();
  };

  // Send-gift tap → open the send-gift sheet in its video-call variant. Flutter
  // builds an `AppTransaction` and calls `NavigationService.showSendGiftSheet`
  // (`:1163-1202`); on the web the sheet carries the `videoCall` target, and the
  // Task-1 service does the peer-resolution + `chatVideoCallInfo` assembly on
  // confirm, so the page only supplies both call users + the call message id. The
  // page owns this (Finding 1) since it holds the call context in `args`.
  const handleSendGift = (): void => {
    SendGiftSheet.show(
      {
        kind: SendGiftTargetType.videoCall,
        callerUser: args.callerUser,
        audienceUser: args.audienceUser,
        callMessageId: args.callMessageId,
      },
      l10n.gift.sendGift,
      undefined,
      screenKey,
    );
  };

  // Localized + interpolated copy for the "calling"/"could not join" lines.
  const audienceName = args.audienceUser.name;
  const callingMessage = l10n.chat.videoCallPage.callingUser.replace(
    /\{name\}/g,
    audienceName,
  );
  const couldNotJoinMessage = l10n.chat.videoCallPage.couldNotJoin.replace(
    /\{name\}/g,
    audienceName,
  );

  return (
    <div
      {...qaAttr({ id: 'video-call', status: pageStatus })}
      style={{
        position: 'relative',
        width: '100%',
        height: '100dvh',
        overflow: 'hidden',
        backgroundColor: theme.background,
      }}
    >
      {/* Video layer — live/connected branch only (Flutter `_videoView()` :541, :863-915). */}
      {!videoCall.audienceDidNotJoin &&
        !videoCall.callEnded &&
        !videoCall.isOpeningChannel && (
          <VideoCallVideoViews
            isCaller={videoCall.isCaller}
            remoteUsers={videoCall.remoteUsers}
            audienceStreamingId={videoCall.audienceStreamingId}
            callerUserStreamingId={videoCall.callerUserStreamingId}
            localCameraTrack={videoCall.localCameraTrack}
            isCameraOff={videoCall.isCameraOff}
            clearScreen={videoCall.clearScreen}
            onToggleClearScreen={videoCall.toggleClearScreen}
          />
        )}

      {/* State views — precedence mirrors Flutter branching (:863-915); `_lastGift` renders separately below. */}
      {videoCall.audienceDidNotJoin ? (
        // Flutter `_audienceUserDidNotJoinWidget` :801-861.
        <VideoCallStateView
          variant={VideoCallStateVariant.audienceNoJoin}
          message={couldNotJoinMessage}
          onClose={goBack}
          closeQaId="video-call-cancel"
        />
      ) : videoCall.callEnded ? (
        // Flutter `_streamingEndedWidget` :739-799.
        <VideoCallStateView
          variant={VideoCallStateVariant.ended}
          message={l10n.chat.videoCallPage.callEnded}
          onClose={goBack}
          closeQaId="video-call-cancel"
        />
      ) : videoCall.isOpeningChannel ? (
        // Flutter `_callingAudienceUserWidget(isCalling: _isCaller)` :713-718, :1037-1110.
        <VideoCallStateView
          variant={VideoCallStateVariant.calling}
          message={callingMessage}
          showMessage={videoCall.isCaller}
          audienceFirebaseId={args.audienceUser.firebaseId}
          onClose={goBack}
          closeQaId="video-call-cancel"
        />
      ) : null}

      {/* Control buttons — live surface only, hidden when cleared (Flutter `_bottomWidget` :1112-1118). */}
      {!videoCall.clearScreen &&
        !videoCall.callEnded &&
        !videoCall.isOpeningChannel &&
        !videoCall.audienceDidNotJoin && (
          <VideoCallControls
            isMute={videoCall.isMute}
            isCameraOff={videoCall.isCameraOff}
            hasCameraTrack={videoCall.localCameraTrack != null}
            cameraCount={videoCall.cameraCount}
            audienceJoined={videoCall.audienceStreamingId != null}
            onSendGift={handleSendGift}
            onToggleMute={videoCall.toggleMute}
            onToggleCamera={videoCall.toggleCamera}
            onSwitchCamera={videoCall.switchCamera}
            onEndCall={() => void handleEndCall()}
          />
        )}

      {/* Last-gift overlay — the transient "{name} sent a gift" banner. Rendered
          unconditionally as a sibling Stack child (Flutter `_lastGift()` :544); its
          only gate is `gift == null` (:558-560), which the component handles by
          returning null. Not gated on callEnded/isOpeningChannel/audienceDidNotJoin —
          those govern the video-view layer (:541), not this overlay, so a gift keeps
          fading over its own 5s window even after the call ends.
          `isCurrentUserSender` mirrors Flutter `:562-566` (`latestLiveGiftInfo.userId
          == currentFirebaseUser.uid`) using the uid threaded into the body.
          Rendered BEFORE the token-renewal overlay so it paints below it, matching
          Flutter's Stack child order `_lastGift()` (:544) < `_renewingTokenWidget()` (:545). */}
      <VideoCallLastGift
        gift={videoCall.latestLiveGift}
        opacity={videoCall.latestLiveGiftOpacity}
        isCurrentUserSender={
          videoCall.latestLiveGift?.userId === currentUserFirebaseId
        }
      />

      {/* Token-renewal overlay — stacks on top (Flutter `_renewingTokenWidget` :720-737, :545). */}
      {videoCall.isRenewingToken && (
        <VideoCallStateView
          variant={VideoCallStateVariant.renewingToken}
          message={l10n.chat.videoCallPage.regeneratingToken}
        />
      )}

      {/* Transparent app-bar back arrow, hidden when cleared (Flutter `_appBar` :534-537, :694-711, :1113). */}
      {!videoCall.clearScreen && (
        <div
          style={{
            position: 'absolute',
            top: responsive.safeArea.top,
            left: responsive.spacing.sm,
            zIndex: 10,
          }}
        >
          <BackIconButton onClick={() => void handleBack()} qa={{ id: 'video-call-back' }} />
        </div>
      )}
    </div>
  );
};

export const VideoCallPage: React.FC = () => {
  const location = useLocation();
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  const currentUser = useSelector((state: RootState) => state.auth.user);

  // Canonical screen-key wiring — captured ONCE on mount (like ChatPage / MediaDetailsPage).
  const { currentScreenKeyRef } = useKeepAlive();
  const screenKeyRef = useRef<string>(
    currentScreenKeyRef.current || generateScreenKey(location.pathname),
  );
  const screenKey = screenKeyRef.current;

  // Snapshot of `location.state` (the route args), taken ONCE at mount. `null`
  // on the deep-link / refresh path — renders the error view, never fetches by id.
  const routeArgs = useMemo<VideoCallRouteArgs | null>(() => {
    const state = location.state as VideoCallRouteArgs | undefined;
    if (
      state &&
      state.callerUser &&
      state.audienceUser &&
      state.groupChatId &&
      state.callMessageId &&
      state.chatUsersInfo
    ) {
      return state;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The Agora client — built ONCE (memoized) so the provider holds a stable
  // instance across renders. `mode: "live"`, `codec: "vp8"`; the broadcaster
  // role is promoted in the hook. Consumed via `useRTCClient()`.
  const client = useMemo<IAgoraRTCClient>(
    () => AgoraRTC.createClient({ mode: 'live', codec: 'vp8' }),
    [],
  );

  const containerStyle = {
    position: 'relative',
    width: '100%',
    height: '100dvh',
    backgroundColor: theme.background,
  } as const;

  // Deep-link / refresh with no route args — surface the error, no by-id fetch
  // (anti-deep-link, like `MediaDetailsPage`). Terminal: no context to call from.
  if (!routeArgs) {
    return (
      <div
        {...qaAttr({ id: 'video-call', status: 'error' })}
        style={{
          ...containerStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: responsive.spacing.base,
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: responsive.safeArea.top,
            left: responsive.spacing.sm,
          }}
        >
          <BackIconButton onClick={() => Navigator.goBack()} qa={{ id: 'video-call-back' }} />
        </div>
        <span
          style={{
            color: theme.text.primary,
            fontSize: responsive.font.base,
            textAlign: 'center',
          }}
        >
          {l10n.chat.videoCallPage.argsMissingError}
        </span>
      </div>
    );
  }

  // Auth-hydrate gate (args present, current user not yet resolved) — transient,
  // so it spins rather than erroring (like `MediaDetailsPage`'s `showFirstLoad`).
  // The hook reads `currentUser` for `isCaller`, so the body is held until resolved.
  if (!currentUser) {
    return (
      <div
        {...qaAttr({ id: 'video-call', status: 'loading' })}
        style={{
          ...containerStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ExpauseLoadingIndicator />
      </div>
    );
  }

  // The provider must wrap the body so `useVideoCall` (`useRTCClient()`) + the
  // video views resolve the client.
  return (
    <AgoraRTCProvider client={client}>
      <VideoCallPageBody
        args={routeArgs}
        screenKey={screenKey}
        currentUserFirebaseId={currentUser.firebaseId}
      />
    </AgoraRTCProvider>
  );
};
